import { filter, includes } from "lodash";
import { useRef, useState } from "react";
import { UserAvatar, UserAvatarStore } from "src/components/ui/avatar/Avatar";
import { searchPeopleRequest } from "src/components/ui/form/peoplePicker/api";
import { SearchUI } from "src/components/ui/Search";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { SearchPeopleType } from "src/types/main";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { getNameAndSurname, nameFromEmail, prefix, removePrefix, shortNameFromEmail } from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { PersonType } from "../type";
import { UserAvatarItem } from "src/components/ui/TGUserAvatarsList";

type SearchInputEmployeeProps = {
  selectedPerson: PersonType | null | undefined;
  changePerson: (person: PersonType | null) => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  classDropdown?: string;
  label?: string;
  unique?: string[];
  allowList?: string[];
};

const isAllowEmail = (email: string, allowList: string[] = []) => {
  return allowList.some((a) => a.toLowerCase() === email.toLowerCase());
};

export function SearchInputEmployee({
  selectedPerson,
  changePerson,
  error,
  disabled = false,
  required = false,
  classDropdown,
  label = "ФИО участника",
  unique,
  allowList,
}: SearchInputEmployeeProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<PersonType[]>([]);

  function handleSearch(value: string) {
    if (!!employees.length && value !== query) {
      setQuery(value); // Сбросить только если ввод изменился и есть что сбрасывать
    } else {
      setQuery(value);
    }
  }

  function resetQuery() {
    changePerson(null);
    setEmployees([]);
    setQuery("");
  }

  useAsyncEffect(async () => {
    let results: PersonType[] = [];

    setLoading(true);

    try {
      if (query.length > 3) {
        const users = (await searchPeopleRequest(query)) as unknown as SearchPeopleType[];

        if (unique && unique.length) {
          const uniqUsers = filter(users, (user) => !includes(unique, removePrefix(user.AccountName, prefix)));

          results = uniqUsers.reduce((acc: PersonType[], user) => {
            const email = removePrefix(user.AccountName, prefix);

            if (allowList && allowList.length > 0 && !isAllowEmail(email, allowList)) {
              return acc;
            }

            const name = user.PreferredName ? getNameAndSurname(user.PreferredName, email) : nameFromEmail(email);
            const shortName = user.PreferredName
              ? getNameAndSurname(user.PreferredName, email, true)
              : shortNameFromEmail(email);

            acc.push({
              DisplayName: name,
              PersonalNumber: Number(user.apiGlobalID || user.abiGlobalID || user.UserName || 0),
              Email: email,
              Department: "",
              Position: "",
              Title: `${name} (${user.apiGlobalID || user.abiGlobalID || user.UserName || "Нет номера"})`,
              ID: user.DocId,
              UserID: user.DocId,
              Avatar: user.PictureURL,
              ShortName: shortName,
            });

            return acc;
          }, [] as PersonType[]);
        } else {
          results = users.reduce((acc: PersonType[], user) => {
            const email = removePrefix(user.AccountName, prefix);

            if (allowList && allowList.length > 0 && !isAllowEmail(email, allowList)) {
              return acc;
            }

            const name = user.PreferredName ? getNameAndSurname(user.PreferredName, email) : nameFromEmail(email);
            const shortName = user.PreferredName
              ? getNameAndSurname(user.PreferredName, email, true)
              : shortNameFromEmail(email);

            acc.push({
              DisplayName: name,
              PersonalNumber: Number(user.apiGlobalID || user.abiGlobalID || user.UserName || 0),
              Email: email,
              Department: "",
              Position: "",
              Title: `${name} (${user.apiGlobalID || user.abiGlobalID || user.UserName || "Нет номера"})`,
              ID: user.DocId,
              UserID: user.DocId,
              Avatar: user.PictureURL,
              ShortName: shortName,
            });

            return acc;
          }, [] as PersonType[]);
        }
      }
      setEmployees(results);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [query]);

  return (
    <FlexRow className="relative flex-wrap mb-5">
      <div className="relative w-full hide-cancel-button">
        <SearchUI
          type="text"
          ref={searchInputRef}
          placeholder={selectedPerson ? "" : "Введите имя сотрудника"}
          defaultValue={selectedPerson ? "" : query}
          error={error}
          require={required}
          disabled={disabled}
          onClick={handleSearch}
          loading={loading}
          label={label}
          resetButton={!!selectedPerson}
          inputClass={twMerge("appearance-none", selectedPerson ? "text-white placeholder:text-white" : "")}
          labelClass="font-normal text-black"
          resetHandle={() => resetQuery()}
        />
        {selectedPerson && (
          <div
            className={twMerge(
              "absolute flex pl-5 py-2 space-x-2.5 bottom-0 left-0 items-center bg-transparent text-black pr-10 w-full",
            )}
          >
            <FlexCol className="shrink-0">
              <UserAvatarItem
                item={{ name: selectedPerson.Title, shortName: selectedPerson.ShortName, id: selectedPerson.ID }}
                classImg="w-7.5 h-7.5 rounded-full object-cover"
                single={true}
              />
              {/* {selectedPerson.Avatar ? (
                <img
                  className={"w-7.5 h-7.5 rounded-full object-cover"}
                  src={`${selectedPerson.Avatar}`}
                  alt={selectedPerson.Title}
                />
              ) : (
                <FlexCol
                  className={twMerge(
                    "object-cover justify-center items-center w-6 h-6 font-medium text-white rounded-full select-none text-[12px] max-sm:text-[10px] bg-darkblue",
                  )}
                >
                  {selectedPerson.ShortName.toUpperCase()}
                </FlexCol>
              )} */}
            </FlexCol>
            {/* <FlexCol className="truncate">{selectedPerson.Title}</FlexCol> */}
          </div>
        )}
      </div>
      {!selectedPerson && employees.length > 0 && (
        <div
          className={twMerge(
            "absolute max-h-64 h-auto z-10 overflow-y-auto rounded-md shadow-custom mt-2.5 bg-white top-[calc(100%+10px)] w-full py-2.5 text-[14px] scrollbar-thin scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-[#D1D5DB]",
            classDropdown,
          )}
        >
          {employees.map((e) => (
            <div
              key={e.ID}
              className={twMerge(
                selectedPerson && e.ID === (selectedPerson as PersonType)?.ID ? "bg-[#F3F4F6]" : "",
                "flex px-5 py-2 space-x-2.5 cursor-pointer items-center hover:bg-[#F3F4F6] text-black",
              )}
              onClick={() => {
                changePerson(e);
                searchInputRef.current!.value = "Новый текст";
              }}
            >
              <FlexCol>
                <UserAvatar avatar={e.Avatar} displayName={e.Title} email={e.Email} shortName={e.ShortName} />
              </FlexCol>
              <FlexCol>{e.Title}</FlexCol>
            </div>
          ))}
        </div>
      )}
    </FlexRow>
  );
}
